package com.evoapps.evoshift.ui.map

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Place
import androidx.compose.material.icons.filled.Remove
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.domain.model.GeoLocation
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.map_attribution
import evoshift.composeapp.generated.resources.settings_map
import evoshift.composeapp.generated.resources.settings_map_pin
import evoshift.composeapp.generated.resources.settings_map_zoom_in
import evoshift.composeapp.generated.resources.settings_map_zoom_out
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.kodein.di.compose.rememberInstance
import kotlin.math.abs
import kotlin.math.roundToInt

/** Where the map looks (SH-132): its centre and whole-number zoom; the dialog moves it to a found place. */
@Stable
class MapViewState(center: GeoLocation, zoom: Int) {
    var center by mutableStateOf(center)
    var zoom by mutableIntStateOf(zoom.coerceIn(WebMercator.MIN_ZOOM, WebMercator.MAX_ZOOM))
        private set

    fun moveTo(location: GeoLocation, zoom: Int? = null) {
        center = location
        zoom?.let { this.zoom = it.coerceIn(WebMercator.MIN_ZOOM, WebMercator.MAX_ZOOM) }
    }

    fun zoomIn() { zoom = (zoom + 1).coerceAtMost(WebMercator.MAX_ZOOM) }
    fun zoomOut() { zoom = (zoom - 1).coerceAtLeast(WebMercator.MIN_ZOOM) }
}

/** Opened from the attribution, as OpenStreetMap's licence asks. */
const val OSM_COPYRIGHT_URL = "https://www.openstreetmap.org/copyright"

/**
 * A slippy map of OpenStreetMap tiles drawn by Compose on every platform (SH-132): drag (or
 * swipe) to pan, pinch, the mouse wheel or the buttons to zoom, click or tap to [onPick] a
 * point. [pin] is drawn where it is. The attribution "© OpenStreetMap contributors" stays in the
 * corner; [onAttribution] should open [OSM_COPYRIGHT_URL] (passed in: inside a dialog window the
 * platform's own LocalUriHandler replaces the app's). Tiles come from the DI's [MapTileSource].
 */
@Composable
fun OsmMap(
    state: MapViewState,
    pin: GeoLocation?,
    onPick: (GeoLocation) -> Unit,
    onAttribution: () -> Unit,
    modifier: Modifier = Modifier
) {
    val source: MapTileSource by rememberInstance()
    val density = LocalDensity.current
    val tileSize = with(density) { OsmTileSource.TILE_PIXELS.dp.toPx() }.toDouble()
    var size by remember { mutableStateOf(IntSize.Zero) }
    val loaded = remember { mutableStateMapOf<TileKey, ImageBitmap>() }
    val pick by rememberUpdatedState(onPick)
    val mapLabel = stringResource(Res.string.settings_map)

    val visible = remember(state.center, state.zoom, size, tileSize) {
        if (size == IntSize.Zero) emptyList()
        else WebMercator.visibleTiles(state.center, state.zoom, tileSize, size.width.toDouble(), size.height.toDouble())
    }
    // Only the tiles in view, only while shown; leaving the view cancels what is still loading.
    LaunchedEffect(visible.map { it.key }.toSet()) {
        val keys = visible.map { it.key }.toSet()
        loaded.keys.retainAll(keys)
        keys.filter { it !in loaded }.forEach { key ->
            val cached = source.cached(key)
            if (cached != null) loaded[key] = cached
            else launch { source.load(key)?.let { loaded[key] = it } }
        }
    }

    Box(
        modifier
            .clip(RoundedCornerShape(12.dp))
            .clipToBounds()
            .background(MapBackground)
            .onSizeChanged { size = it }
            .semantics { contentDescription = mapLabel }
            .pointerInput(state, tileSize) {
                var scale = 1f
                detectTransformGestures { _, pan, gestureZoom, _ ->
                    state.center = WebMercator.shift(state.center, -pan.x.toDouble(), -pan.y.toDouble(), state.zoom, tileSize)
                    scale *= gestureZoom
                    if (scale > PINCH_STEP) { state.zoomIn(); scale = 1f }
                    if (scale < 1 / PINCH_STEP) { state.zoomOut(); scale = 1f }
                }
            }
            .pointerInput(state, tileSize) {
                detectTapGestures { offset ->
                    pick(
                        WebMercator.fromView(
                            offset.x.toDouble(), offset.y.toDouble(), state.center, state.zoom, tileSize,
                            size.width.toDouble(), size.height.toDouble()
                        )
                    )
                }
            }
            .pointerInput(state) {
                // The mouse wheel or a trackpad: one zoom step per notch, small trackpad deltas add up.
                var wheel = 0f
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        if (event.type != PointerEventType.Scroll) continue
                        wheel += event.changes.sumOf { it.scrollDelta.y.toDouble() }.toFloat()
                        if (abs(wheel) >= 1f) {
                            if (wheel < 0) state.zoomIn() else state.zoomOut()
                            wheel = 0f
                        }
                        event.changes.forEach { it.consume() }
                    }
                }
            }
    ) {
        Canvas(Modifier.fillMaxSize()) {
            val side = tileSize.roundToInt()
            visible.forEach { tile ->
                val at = IntOffset(tile.left.roundToInt(), tile.top.roundToInt())
                val image = loaded[tile.key]
                if (image != null) {
                    drawImage(image, dstOffset = at, dstSize = IntSize(side, side))
                } else {
                    // Until the tile arrives (or in tests, where none do): the grid of the tiles.
                    drawRect(MapGrid, topLeft = androidx.compose.ui.geometry.Offset(at.x.toFloat(), at.y.toFloat()),
                        size = androidx.compose.ui.geometry.Size(side.toFloat(), side.toFloat()),
                        style = androidx.compose.ui.graphics.drawscope.Stroke(width = 1f))
                }
            }
        }

        if (pin != null && size != IntSize.Zero) {
            val at = WebMercator.toView(pin, state.center, state.zoom, tileSize, size.width.toDouble(), size.height.toDouble())
            val pinSize = with(density) { PIN_SIZE.dp.roundToPx() }
            if (at.x in -pinSize.toDouble()..(size.width + pinSize).toDouble() && at.y in 0.0..(size.height + pinSize).toDouble()) {
                Icon(
                    Icons.Default.Place,
                    contentDescription = stringResource(Res.string.settings_map_pin),
                    tint = PinColor,
                    // The icon's tip is at the bottom centre: that is the point.
                    modifier = Modifier
                        .size(PIN_SIZE.dp)
                        .offset { IntOffset(at.x.roundToInt() - pinSize / 2, at.y.roundToInt() - pinSize) }
                )
            }
        }

        Column(Modifier.align(Alignment.TopEnd).padding(6.dp)) {
            MapButton(Icons.Default.Add, stringResource(Res.string.settings_map_zoom_in)) { state.zoomIn() }
            MapButton(Icons.Default.Remove, stringResource(Res.string.settings_map_zoom_out)) { state.zoomOut() }
        }

        Text(
            stringResource(Res.string.map_attribution),
            color = Color(0xFF1F2937),
            fontSize = 11.sp,
            style = MaterialTheme.typography.labelSmall,
            modifier = Modifier
                .align(Alignment.BottomEnd)
                .background(Color.White.copy(alpha = 0.8f), RoundedCornerShape(topStart = 6.dp))
                .clickable(onClick = onAttribution)
                .padding(horizontal = 6.dp, vertical = 2.dp)
        )
    }
}

@Composable
private fun MapButton(icon: androidx.compose.ui.graphics.vector.ImageVector, description: String, onClick: () -> Unit) {
    IconButtonWithTooltip(
        onClick = onClick,
        modifier = Modifier.padding(2.dp).size(36.dp).background(Color.White.copy(alpha = 0.9f), RoundedCornerShape(8.dp)),
        tooltip = description
    ) {
        Icon(icon, contentDescription = description, tint = Color(0xFF1F2937))
    }
}

private const val PIN_SIZE = 40
private const val PINCH_STEP = 1.5f
private val MapBackground = Color(0xFFE5E3DF)
private val MapGrid = Color(0xFFCFCBC4)
private val PinColor = Color(0xFFD32F2F)
